import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { useCurrentView } from '../hooks/useCurrentView';
import type { FoundationFocus } from '../constants/foundationChecklists';

/**
 * ⭐ The in-area SETUP GUIDE (Growth Foundations, 2026-10-05).
 *
 * A checklist's "Open Hydration →" starts it; the Domain Area shows a slim bar
 * with the current step while it is set. It points at the area's own tools — it
 * holds no copy of their forms or data.
 *
 * ⚠️⚠️ A DIRECT, EVERYDAY VISIT NEVER SHOWS IT. Only `openFoundationItem` sets
 * `active`, and leaving the Domain page clears it (the subscription below) — so
 * the next time someone opens Nourishment from the Domains rail, no guide.
 * ⚠️ Cleared by the VIEW changing, not by a component unmounting: StrictMode
 * runs every effect's cleanup once on mount, which would have cleared it before
 * the bar ever drew.
 * ⚠️ Per tab (sessionStorage), and it records nothing: dismissing it abandons no
 * saved setup.
 */
export interface FoundationGuide {
  checklistId: string;
  itemId: string;
  domainId: string;
  areaId: string;
  focus?: FoundationFocus;
}

interface GuideState {
  active: FoundationGuide | null;
  start: (guide: FoundationGuide) => void;
  /** Move to another step of the same checklist (the bar's "Next"). */
  goTo: (itemId: string, focus?: FoundationFocus) => void;
  clear: () => void;
}

const session: StateStorage = {
  getItem: n => { try { return sessionStorage.getItem(n); } catch { return null; } },
  setItem: (n, v) => { try { sessionStorage.setItem(n, v); } catch { /* refused */ } },
  removeItem: n => { try { sessionStorage.removeItem(n); } catch { /* refused */ } },
};

export const useFoundationGuide = create<GuideState>()(
  persist(
    set => ({
      active: null,
      start: guide => set({ active: guide }),
      goTo: (itemId, focus) => set(s => (s.active ? { active: { ...s.active, itemId, focus } } : s)),
      clear: () => set({ active: null }),
    }),
    { name: 'metanoia-foundation-guide', storage: createJSONStorage(() => session) },
  ),
);

// Leaving the Domain page ends the guided visit.
useCurrentView.subscribe(s => {
  if (s.currentView !== 'domain' && useFoundationGuide.getState().active) {
    useFoundationGuide.getState().clear();
  }
});
